import type {
  GSplatBindingResourceSource,
  GSplatResourceLease,
  IGSplatHandleBinding,
  IGSplatHandleBindingFactory,
} from '@/plugins/gsplat/GSplatHandleBinding'
import type { GSplatProcessorBinding } from '@/plugins/gsplat/GSplatProcessor'
import type { GSplatSystemProcessorBinding } from '@/plugins/gsplat/GSplatSystem'
import type { GSplatAssetRecord, GSplatAssetStore } from '@/plugins/gsplat/system/GSplatAssetStore'
import type {
  GaussianSplatHandle,
  GaussianSplatLoadRequest,
} from '@/plugins/gsplat/types/PluginTypes'
import type { AssetId } from '@/shared/types/core'

export interface GSplatHandleCommandCallbacks {
  retireGeneration(id: AssetId, generation: number): void
}

/**
 * 对应旧 ModelOperations 与 façade 中的 Handle 命令职责。
 *
 * 该服务只操作共享 binding/Resource/Placement 状态，不读取 graphics backend，也不拥有 loader。
 */
export class GSplatHandleCommandService {
  constructor(
    private readonly store: GSplatAssetStore,
    private readonly bindingFactory: IGSplatHandleBindingFactory,
    private readonly createAssetId: () => AssetId,
    private readonly callbacks: GSplatHandleCommandCallbacks
  ) {}

  createId(): AssetId {
    return this.createAssetId()
  }

  create(
    id: AssetId,
    request: Readonly<GaussianSplatLoadRequest>,
    resource: GSplatResourceLease,
    generation: number,
    source: GSplatBindingResourceSource = { kind: 'asset', assetId: id }
  ): GaussianSplatHandle {
    let binding: IGSplatHandleBinding | undefined
    try {
      binding = this.bindingFactory.create({
        id,
        request,
        resource,
        generation,
        source,
      })
      this.validateBinding(id, resource, binding)
      this.store.set(id, { request, binding })
      return binding.handle
    } catch (error) {
      if (binding) binding.dispose()
      else resource.dispose()
      throw error
    }
  }

  commitReplacement(
    id: AssetId,
    token: number,
    request: Readonly<GaussianSplatLoadRequest>,
    resource: GSplatResourceLease
  ): GaussianSplatHandle {
    const record = this.requireAsset(id, 'replace')
    const oldGeneration = record.binding.generation
    const committed = record.binding.replaceResource({
      token,
      resource,
      source: { kind: 'asset', assetId: id },
      url: request.url,
      name: request.name,
    })
    if (!committed) throw abortError()
    record.request = request
    this.callbacks.retireGeneration(id, oldGeneration)
    return record.binding.handle
  }

  replaceWithResource(
    id: AssetId,
    resource: GSplatResourceLease,
    metadata: { readonly url?: string; readonly name?: string }
  ): GaussianSplatHandle {
    const record = this.store.get(id)
    if (!record) {
      resource.dispose()
      throw new Error(`GSplatSystem: direct Resource replace 未找到资产 "${id}"`)
    }
    const token = record.binding.beginResourceReplacement()
    const oldGeneration = record.binding.generation
    const committed = record.binding.replaceResource({
      token,
      resource,
      source: { kind: 'direct' },
      url: metadata.url,
      name: metadata.name,
    })
    if (!committed) throw abortError()
    if (metadata.url !== undefined) {
      record.request = Object.freeze({
        ...record.request,
        url: metadata.url,
        name: metadata.name,
      })
    }
    this.callbacks.retireGeneration(id, oldGeneration)
    return record.binding.handle
  }

  mount(handle: GaussianSplatHandle): void {
    const record = this.requireHandle(handle)
    if (!record.binding.mounted) record.binding.mount()
  }

  unmount(id: AssetId): void {
    const record = this.store.get(id)
    if (record?.binding.mounted) record.binding.unmount()
  }

  setVisibility(id: AssetId, visible: boolean): void {
    this.store.get(id)?.binding.setVisibility(visible)
  }

  get(id: AssetId): GaussianSplatHandle | undefined {
    return this.store.get(id)?.binding.handle
  }

  getAll(): GaussianSplatHandle[] {
    return this.store.values().map((record) => record.binding.handle)
  }

  getByUrl(url: string): GaussianSplatHandle | undefined {
    return this.store.getByUrl(url)?.binding.handle
  }

  clone(sourceId: AssetId, cloneId: AssetId = this.createAssetId()): GaussianSplatHandle {
    if (this.store.isReserved(cloneId)) {
      throw new Error(`GSplatSystem: clone asset id "${cloneId}" 已存在或正在加载`)
    }
    const source = this.store.get(sourceId)
    if (!source) throw new Error(`GSplatSystem: clone 未找到资产 "${sourceId}"`)

    const request = Object.freeze({ ...source.request, id: cloneId })
    let lease: GSplatResourceLease | undefined = source.binding.resource.acquire()
    let binding: IGSplatHandleBinding | undefined
    try {
      const stagedResource = lease
      binding = this.bindingFactory.create({
        id: cloneId,
        request,
        resource: lease,
        generation: this.store.allocateGeneration(),
        source: { kind: 'asset', assetId: cloneId },
        state: source.binding.snapshotState(),
      })
      lease = undefined
      this.validateBinding(cloneId, stagedResource, binding)
      this.store.set(cloneId, { request, binding })
      binding = undefined
      return this.store.get(cloneId)!.binding.handle
    } catch (error) {
      binding?.dispose()
      lease?.dispose()
      throw error
    }
  }

  resolveProcessorBinding(binding: GSplatSystemProcessorBinding): GSplatProcessorBinding {
    if (binding.resource) {
      return Object.freeze({
        resource: binding.resource,
        ...(binding.handle ? { handleBinding: this.requireHandle(binding.handle).binding } : {}),
        ...(binding.streams ? { streams: Object.freeze([...binding.streams]) } : {}),
      })
    }
    if (!binding.handle) {
      throw new Error('GSplatSystem: processor binding 缺少 Resource/Handle')
    }
    return Object.freeze({
      handleBinding: this.requireHandle(binding.handle).binding,
      ...(binding.streams ? { streams: Object.freeze([...binding.streams]) } : {}),
    })
  }

  requireAsset(id: AssetId, operation: string): GSplatAssetRecord {
    const record = this.store.get(id)
    if (!record) throw new Error(`GSplatSystem: ${operation} 未找到资产 "${id}"`)
    return record
  }

  disposeAsset(id: AssetId): void {
    const record = this.store.delete(id)
    if (!record) return
    this.callbacks.retireGeneration(id, record.binding.generation)
    if (record.binding.mounted) record.binding.unmount()
    record.binding.dispose()
  }

  private requireHandle(handle: GaussianSplatHandle): GSplatAssetRecord {
    const record = this.store.get(handle.id)
    if (!record || record.binding.handle !== handle) {
      throw new Error(`GSplatSystem: handle "${handle.id}" 不属于当前 System`)
    }
    return record
  }

  private validateBinding(
    id: AssetId,
    resource: GSplatResourceLease,
    binding: IGSplatHandleBinding
  ): void {
    if (binding.handle.id !== id) {
      throw new Error(`GSplatSystem: binding handle id "${binding.handle.id}" 与 "${id}" 不一致`)
    }
    if (binding.handle.kind !== 'gaussian-splat') {
      throw new Error('GSplatSystem: binding handle kind 必须为 "gaussian-splat"')
    }
    if (binding.resource !== resource) {
      throw new Error('GSplatSystem: binding 必须接管当前 staged Resource lease')
    }
  }
}

function abortError(): DOMException {
  return new DOMException('Aborted', 'AbortError')
}
